import { useMemo, useState } from "react";
import { Select, InputNumber, Button, Popover, Tooltip, Badge } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import SerialKillerDetailsCollapse from "./SerialKillerDetailsCollapse";
import type { SerialKillerListItemDTO } from "@shared/contracts";
import styled from "styled-components";

export interface KillerSelectProps {
  killers: SerialKillerListItemDTO[];
  loading?: boolean;
  value?: string | null;
  onChange: (v: string | null) => void;
}

const StyledSelect = styled(Select)`
  width: 100%;
  min-width: 260px;

  .ant-select-selector {
    height: 34px !important;
    display: flex;
    align-items: center;
    padding: 0 12px !important;
    background: #393e46 !important;
    border-color: #222831 !important;
    color: #eeeeee !important;
    border-radius: 8px;
  }

  .ant-select-selection-placeholder {
    color: #eeeeee !important;
    opacity: 0.8;
  }
  .ant-select-selection-item {
    color: #eeeeee !important;
    line-height: 34px !important;
  }

  .ant-select-selection-search-input {
    height: 34px !important;
  }

  .ant-select-arrow svg,
  .ant-select-clear .anticon svg {
    fill: #eeeeee;
    color: #eeeeee;
  }

  &:hover .ant-select-selector {
    border-color: #8e1616 !important;
  }
  &.ant-select-focused .ant-select-selector {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }

  .ant-select-dropdown {
    background: #393e46;
    color: #eeeeee;
    border: 1px solid #222831;
  }
  .ant-select-item {
    color: #eeeeee;
  }
  .ant-select-item-option-active,
  .ant-select-item-option-selected {
    background: #222831;
    color: #eeeeee;
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
`;

const FilterTriggerButton = styled(Button)<{ $active: boolean }>`
  height: 34px;
  min-width: 34px;
  width: 34px;
  border-radius: 8px;
  padding: 0;
  background: #393e46 !important;
  color: #eeeeee !important;
  border: 1px solid #222831 !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  box-shadow: none !important;

  &:hover {
    background: #222831 !important;
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2) !important;
  }

  svg {
    height: 16px;
    width: 16px;
  }
`;

const FilterContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: #2a2f38;
  border: 1px solid #222831;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  color: #eeeeee;
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 2px solid rgba(142, 22, 22, 0.7);
`;

const FilterTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #eeeeee;
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #8e1616;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(238, 238, 238, 0.72);
`;

const FilterSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    background: #393e46 !important;
    border-color: #222831 !important;
    color: #eeeeee !important;
    border-radius: 8px !important;
    min-height: 32px !important;
    box-shadow: none !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: #eeeeee !important;
  }

  .ant-select-arrow,
  .ant-select-clear {
    color: #eeeeee !important;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }

  .ant-select-dropdown {
    background: #393e46;
    border: 1px solid #222831;
    border-radius: 8px;
  }

  .ant-select-item {
    color: #eeeeee;
  }

  .ant-select-item-option-active,
  .ant-select-item-option-selected {
    background: #222831 !important;
    color: #ffffff;
  }
`;

const FilterNumber = styled(InputNumber)`
  width: 100%;

  &.ant-input-number,
  .ant-input-number {
    width: 100% !important;
    height: 32px !important;
    background: #393e46 !important;
    border: 1px solid #222831 !important;
    color: #eeeeee !important;
    border-radius: 8px !important;
    box-shadow: none !important;
  }

  .ant-input-number-input {
    height: 30px !important;
    background: #393e46 !important;
    color: #eeeeee !important;
    padding: 0 11px !important;
    font-size: 14px !important;
    -webkit-text-fill-color: #eeeeee !important;
  }

  .ant-input-number-input::placeholder {
    color: #eeeeee !important;
    opacity: 0.7;
  }

  .ant-input-number-handler-wrap {
    display: none !important;
  }

  .ant-input-number-handler {
    display: none !important;
  }

  &:hover .ant-input-number,
  &.ant-input-number-focused .ant-input-number {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2) !important;
  }
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ResetButton = styled(Button)`
  background: #393e46 !important;
  color: #eeeeee !important;
  border: none !important;
  border-radius: 8px;

  &:hover {
    color: #8e1616 !important;
    background: #222831 !important;
  }
`;

interface KillerFilterState {
  statuses: string[];
  minVictims: number | null;
  maxVictims: number | null;
  activeFrom: number | null;
  activeTo: number | null;
}

const EMPTY_FILTER: KillerFilterState = {
  statuses: [],
  minVictims: null,
  maxVictims: null,
  activeFrom: null,
  activeTo: null,
};

function matchesKillerFilter(
  killer: SerialKillerListItemDTO,
  filter: KillerFilterState,
) {
  const statusMatch =
    filter.statuses.length === 0 ||
    (killer.status != null && filter.statuses.includes(killer.status));

  const victimsMatch =
    (filter.minVictims == null ||
      (killer.numberOfVictims != null &&
        killer.numberOfVictims >= filter.minVictims)) &&
    (filter.maxVictims == null ||
      (killer.numberOfVictims != null &&
        killer.numberOfVictims <= filter.maxVictims));

  const activeYearStart = killer.activeYearStart ?? null;
  const activeYearEnd = killer.activeYearEnd ?? null;

  const activeFromMatch =
    filter.activeFrom == null ||
    (activeYearStart != null && activeYearStart >= filter.activeFrom) ||
    (activeYearEnd != null && activeYearEnd >= filter.activeFrom);

  const activeToMatch =
    filter.activeTo == null ||
    (activeYearEnd != null && activeYearEnd <= filter.activeTo) ||
    (activeYearStart != null && activeYearStart <= filter.activeTo);

  return statusMatch && victimsMatch && activeFromMatch && activeToMatch;
}

export default function KillerSelect({
  killers,
  loading,
  value,
  onChange,
}: KillerSelectProps) {
  const [filter, setFilter] = useState<KillerFilterState>(EMPTY_FILTER);

  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(
          killers
            .map((killer) => killer.status)
            .filter(
              (
                status,
              ): status is NonNullable<SerialKillerListItemDTO["status"]> =>
                status != null,
            ),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [killers],
  );

  const filteredKillers = useMemo(
    () =>
      [...killers]
        .filter((killer) => matchesKillerFilter(killer, filter))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [killers, filter],
  );

  const activeFilterCount =
    (filter.statuses.length > 0 ? 1 : 0) +
    (filter.minVictims != null || filter.maxVictims != null ? 1 : 0) +
    (filter.activeFrom != null || filter.activeTo != null ? 1 : 0);

  const filterContent = (
    <FilterContent>
      <FilterHeader>
        <FilterTitle>Filter killers</FilterTitle>
        {activeFilterCount > 0 && (
          <FilterBadge>{activeFilterCount}</FilterBadge>
        )}
      </FilterHeader>

      <FilterGrid>
        <FilterField>
          <FieldLabel>Status</FieldLabel>
          <FilterSelect
            mode="multiple"
            allowClear
            size="small"
            placeholder="Any status"
            value={filter.statuses}
            onChange={(values) =>
              setFilter((current) => ({
                ...current,
                statuses: Array.isArray(values) ? (values as string[]) : [],
              }))
            }
            options={statusOptions.map((status) => ({
              value: status,
              label: status,
            }))}
          />
        </FilterField>

        <FilterField>
          <FieldLabel>Victims</FieldLabel>
          <FilterGrid>
            <FilterNumber
              size="small"
              min={0}
              placeholder="Min"
              value={filter.minVictims}
              onChange={(value) =>
                setFilter((current) => ({
                  ...current,
                  minVictims: typeof value === "number" ? value : null,
                }))
              }
            />
            <FilterNumber
              size="small"
              min={0}
              placeholder="Max"
              value={filter.maxVictims}
              onChange={(value) =>
                setFilter((current) => ({
                  ...current,
                  maxVictims: typeof value === "number" ? value : null,
                }))
              }
            />
          </FilterGrid>
        </FilterField>

        <FilterField>
          <FieldLabel>Active from</FieldLabel>
          <FilterNumber
            size="small"
            min={0}
            placeholder="Start year"
            value={filter.activeFrom}
            onChange={(value) =>
              setFilter((current) => ({
                ...current,
                activeFrom: typeof value === "number" ? value : null,
              }))
            }
          />
        </FilterField>

        <FilterField>
          <FieldLabel>Active to</FieldLabel>
          <FilterNumber
            size="small"
            min={0}
            placeholder="End year"
            value={filter.activeTo}
            onChange={(value) =>
              setFilter((current) => ({
                ...current,
                activeTo: typeof value === "number" ? value : null,
              }))
            }
          />
        </FilterField>
      </FilterGrid>

      <FilterActions>
        <ResetButton size="small" onClick={() => setFilter(EMPTY_FILTER)}>
          Reset
        </ResetButton>
      </FilterActions>
    </FilterContent>
  );

  return (
    <>
      <FilterWrapper>
        <StyledSelect
          showSearch
          allowClear
          placeholder="Filter by serial killer"
          options={filteredKillers.map((k) => ({
            value: k.id,
            label: k.alias ? `${k.name} (${k.alias})` : k.name,
          }))}
          loading={loading}
          value={value ?? undefined}
          onChange={(val) => onChange((val ?? null) as string | null)}
          filterOption={(input, option) =>
            (option?.label as string)
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
        />

        <Tooltip
          title={
            activeFilterCount > 0
              ? `Killer filters (${activeFilterCount} active)`
              : "Killer filters"
          }
        >
          <Popover
            content={filterContent}
            trigger="click"
            placement="bottomRight"
            overlayStyle={{
              ["--antd-arrow-background-color" as any]: "#2a2f38",
            }}
            overlayInnerStyle={{
              padding: 0,
              background: "transparent",
              borderRadius: 12,
            }}
          >
            <Badge
              count={activeFilterCount > 0 ? activeFilterCount : 0}
              color="#8e1616"
              size="small"
              overflowCount={99}
              showZero={false}
            >
              <FilterTriggerButton
                aria-label="Serial killer filters"
                $active={activeFilterCount > 0}
                icon={<FilterOutlined />}
              />
            </Badge>
          </Popover>
        </Tooltip>
      </FilterWrapper>
      {value && <SerialKillerDetailsCollapse killerId={value} />}
    </>
  );
}
